import type { Dataset } from '../types';
import { tagKey } from './columns';

export interface StudentRow {
  name: string;
  height_cm: number;
  shoe_size_eu: number;
  study_hours: number;
  test_score: number;
  sports_played: readonly string[];
  eye_color: string;
  commute_min: number;
  grade_year: string;
  homework_effort: string;
}

const ORDINAL_YEARS = ['Year 9', 'Year 10', 'Year 11', 'Year 12'] as const;
const ORDINAL_EFFORT = ['never', 'rarely', 'sometimes', 'often', 'always'] as const;
const SPORT_TAGS = ['football', 'basketball', 'volleyball', 'swimming', 'none'] as const;

const ROWS: StudentRow[] = [
  {
    name: 'Student 01',
    height_cm: 152.4,
    shoe_size_eu: 36,
    study_hours: 1.5,
    test_score: 58,
    sports_played: ['football'],
    eye_color: 'brown',
    commute_min: 10,
    grade_year: 'Year 9',
    homework_effort: 'sometimes',
  },
  {
    name: 'Student 02',
    height_cm: 155.1,
    shoe_size_eu: 37,
    study_hours: 4.0,
    test_score: 72,
    sports_played: ['basketball'],
    eye_color: 'blue',
    commute_min: 22,
    grade_year: 'Year 9',
    homework_effort: 'often',
  },
  {
    name: 'Student 03',
    height_cm: 158.8,
    shoe_size_eu: 37,
    study_hours: 2.0,
    test_score: 63,
    sports_played: ['football', 'basketball'],
    eye_color: 'green',
    commute_min: 35,
    grade_year: 'Year 9',
    homework_effort: 'rarely',
  },
  {
    name: 'Student 04',
    height_cm: 160.0,
    shoe_size_eu: 38,
    study_hours: 6.5,
    test_score: 81,
    sports_played: ['volleyball'],
    eye_color: 'brown',
    commute_min: 18,
    grade_year: 'Year 9',
    homework_effort: 'often',
  },
  {
    name: 'Student 05',
    height_cm: 162.2,
    shoe_size_eu: 38,
    study_hours: 0.5,
    test_score: 32,
    sports_played: ['none'],
    eye_color: 'hazel',
    commute_min: 45,
    grade_year: 'Year 9',
    homework_effort: 'never',
  },
  {
    name: 'Student 06',
    height_cm: 164.5,
    shoe_size_eu: 39,
    study_hours: 3.0,
    test_score: 69,
    sports_played: ['football', 'volleyball'],
    eye_color: 'brown',
    commute_min: 12,
    grade_year: 'Year 10',
    homework_effort: 'sometimes',
  },
  {
    name: 'Student 07',
    height_cm: 166.7,
    shoe_size_eu: 39,
    study_hours: 8.0,
    test_score: 88,
    sports_played: ['basketball', 'volleyball'],
    eye_color: 'blue',
    commute_min: 20,
    grade_year: 'Year 10',
    homework_effort: 'always',
  },
  {
    name: 'Student 08',
    height_cm: 167.0,
    shoe_size_eu: 40,
    study_hours: 5.0,
    test_score: 75,
    sports_played: ['football'],
    eye_color: 'green',
    commute_min: 28,
    grade_year: 'Year 10',
    homework_effort: 'often',
  },
  {
    name: 'Student 09',
    height_cm: 168.3,
    shoe_size_eu: 40,
    study_hours: 2.5,
    test_score: 66,
    sports_played: ['football', 'basketball'],
    eye_color: 'brown',
    commute_min: 30,
    grade_year: 'Year 10',
    homework_effort: 'sometimes',
  },
  {
    name: 'Student 10',
    height_cm: 169.1,
    shoe_size_eu: 40,
    study_hours: 10.5,
    test_score: 92,
    sports_played: ['swimming'],
    eye_color: 'hazel',
    commute_min: 8,
    grade_year: 'Year 10',
    homework_effort: 'always',
  },
  {
    name: 'Student 11',
    height_cm: 170.0,
    shoe_size_eu: 41,
    study_hours: 4.5,
    test_score: 74,
    sports_played: ['basketball'],
    eye_color: 'brown',
    commute_min: 15,
    grade_year: 'Year 11',
    homework_effort: 'often',
  },
  {
    name: 'Student 12',
    height_cm: 171.2,
    shoe_size_eu: 41,
    study_hours: 7.0,
    test_score: 83,
    sports_played: ['football', 'swimming'],
    eye_color: 'blue',
    commute_min: 25,
    grade_year: 'Year 11',
    homework_effort: 'often',
  },
  {
    name: 'Student 13',
    height_cm: 172.5,
    shoe_size_eu: 42,
    study_hours: 1.0,
    test_score: 41,
    sports_played: ['volleyball'],
    eye_color: 'green',
    commute_min: 50,
    grade_year: 'Year 11',
    homework_effort: 'rarely',
  },
  {
    name: 'Student 14',
    height_cm: 173.8,
    shoe_size_eu: 42,
    study_hours: 12.0,
    test_score: 95,
    sports_played: ['volleyball'],
    eye_color: 'brown',
    commute_min: 14,
    grade_year: 'Year 11',
    homework_effort: 'always',
  },
  {
    name: 'Student 15',
    height_cm: 174.9,
    shoe_size_eu: 42,
    study_hours: 3.5,
    test_score: 67,
    sports_played: ['football', 'basketball'],
    eye_color: 'hazel',
    commute_min: 32,
    grade_year: 'Year 11',
    homework_effort: 'sometimes',
  },
  {
    name: 'Student 16',
    height_cm: 175.5,
    shoe_size_eu: 43,
    study_hours: 6.0,
    test_score: 79,
    sports_played: ['basketball', 'volleyball'],
    eye_color: 'brown',
    commute_min: 22,
    grade_year: 'Year 11',
    homework_effort: 'often',
  },
  {
    name: 'Student 17',
    height_cm: 176.2,
    shoe_size_eu: 43,
    study_hours: 9.0,
    test_score: 90,
    sports_played: ['football'],
    eye_color: 'blue',
    commute_min: 17,
    grade_year: 'Year 12',
    homework_effort: 'always',
  },
  {
    name: 'Student 18',
    height_cm: 177.0,
    shoe_size_eu: 43,
    study_hours: 2.0,
    test_score: 60,
    sports_played: ['football'],
    eye_color: 'green',
    commute_min: 40,
    grade_year: 'Year 12',
    homework_effort: 'rarely',
  },
  {
    name: 'Student 19',
    height_cm: 178.4,
    shoe_size_eu: 44,
    study_hours: 5.5,
    test_score: 76,
    sports_played: ['basketball'],
    eye_color: 'brown',
    commute_min: 24,
    grade_year: 'Year 12',
    homework_effort: 'often',
  },
  {
    name: 'Student 20',
    height_cm: 179.1,
    shoe_size_eu: 44,
    study_hours: 22.0,
    test_score: 99,
    sports_played: ['football', 'basketball', 'swimming'],
    eye_color: 'hazel',
    commute_min: 11,
    grade_year: 'Year 12',
    homework_effort: 'always',
  },
  {
    name: 'Student 21',
    height_cm: 180.0,
    shoe_size_eu: 44,
    study_hours: 4.0,
    test_score: 71,
    sports_played: ['volleyball'],
    eye_color: 'brown',
    commute_min: 27,
    grade_year: 'Year 12',
    homework_effort: 'often',
  },
  {
    name: 'Student 22',
    height_cm: 181.3,
    shoe_size_eu: 45,
    study_hours: 7.5,
    test_score: 85,
    sports_played: ['football', 'basketball', 'volleyball'],
    eye_color: 'blue',
    commute_min: 19,
    grade_year: 'Year 12',
    homework_effort: 'often',
  },
  {
    name: 'Student 23',
    height_cm: 182.0,
    shoe_size_eu: 45,
    study_hours: 3.0,
    test_score: 64,
    sports_played: ['basketball', 'volleyball'],
    eye_color: 'green',
    commute_min: 33,
    grade_year: 'Year 10',
    homework_effort: 'sometimes',
  },
  {
    name: 'Student 24',
    height_cm: 183.4,
    shoe_size_eu: 45,
    study_hours: 1.5,
    test_score: 55,
    sports_played: ['volleyball', 'swimming'],
    eye_color: 'brown',
    commute_min: 48,
    grade_year: 'Year 11',
    homework_effort: 'rarely',
  },
  {
    name: 'Student 25',
    height_cm: 184.0,
    shoe_size_eu: 45,
    study_hours: 6.0,
    test_score: 78,
    sports_played: ['football'],
    eye_color: 'hazel',
    commute_min: 16,
    grade_year: 'Year 12',
    homework_effort: 'often',
  },
  {
    name: 'Student 26',
    height_cm: 185.2,
    shoe_size_eu: 46,
    study_hours: 0.5,
    test_score: 38,
    sports_played: ['football'],
    eye_color: 'brown',
    commute_min: 55,
    grade_year: 'Year 9',
    homework_effort: 'never',
  },
  {
    name: 'Student 27',
    height_cm: 186.0,
    shoe_size_eu: 46,
    study_hours: 8.5,
    test_score: 87,
    sports_played: ['football', 'volleyball', 'swimming'],
    eye_color: 'blue',
    commute_min: 21,
    grade_year: 'Year 12',
    homework_effort: 'always',
  },
  {
    name: 'Student 28',
    height_cm: 186.9,
    shoe_size_eu: 46,
    study_hours: 4.5,
    test_score: 73,
    sports_played: ['basketball'],
    eye_color: 'green',
    commute_min: 29,
    grade_year: 'Year 11',
    homework_effort: 'often',
  },
  {
    name: 'Student 29',
    height_cm: 187.5,
    shoe_size_eu: 46,
    study_hours: 11.0,
    test_score: 93,
    sports_played: ['football', 'basketball'],
    eye_color: 'brown',
    commute_min: 13,
    grade_year: 'Year 11',
    homework_effort: 'always',
  },
  {
    name: 'Student 30',
    height_cm: 188.7,
    shoe_size_eu: 46,
    study_hours: 5.0,
    test_score: 77,
    sports_played: ['volleyball'],
    eye_color: 'hazel',
    commute_min: 60,
    grade_year: 'Year 12',
    homework_effort: 'sometimes',
  },
];

export const MASTER_SURVEY: Dataset<StudentRow> = {
  id: 'master-survey',
  labelKey: 'dataset.master-survey.label',
  rows: ROWS,
  columns: [
    { key: 'name', labelKey: 'column.name.label', type: 'qualitative-nominal' },
    {
      key: 'height_cm',
      labelKey: 'column.height_cm.label',
      type: 'quantitative-continuous',
      unit: 'cm',
    },
    { key: 'shoe_size_eu', labelKey: 'column.shoe_size_eu.label', type: 'quantitative-discrete' },
    {
      key: 'study_hours',
      labelKey: 'column.study_hours.label',
      type: 'quantitative-continuous',
      unit: 'h',
    },
    {
      key: 'test_score',
      labelKey: 'column.test_score.label',
      type: 'quantitative-discrete',
      unit: '%',
    },
    {
      key: 'sports_played',
      labelKey: 'column.sports_played.label',
      type: 'qualitative-multi',
      tags: SPORT_TAGS,
      tagLabelKey: (tag) => tagKey(tag),
    },
    { key: 'eye_color', labelKey: 'column.eye_color.label', type: 'qualitative-nominal' },
    {
      key: 'commute_min',
      labelKey: 'column.commute_min.label',
      type: 'quantitative-continuous',
      unit: 'min',
    },
    {
      key: 'grade_year',
      labelKey: 'column.grade_year.label',
      type: 'qualitative-ordinal',
      ordinalOrder: ORDINAL_YEARS,
    },
    {
      key: 'homework_effort',
      labelKey: 'column.homework_effort.label',
      type: 'qualitative-ordinal',
      ordinalOrder: ORDINAL_EFFORT,
    },
  ],
};
